import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'

import { toaster } from '@/components/ui/toaster'
import { WBDApi } from '@/open-api'
import type { DesignDto, DesignMockupDto, GetDesignsResponseDto } from '@/open-api/Api'

export type Design = DesignDto
export type DesignMockup = DesignMockupDto
export type DesignsResponse = GetDesignsResponseDto

export interface GetDesignsQueryDto {
  skip?: number
  take?: number
  isFavorite?: boolean
}

const DESIGNS_QUERY_KEY = 'designs'

export const useDesigns = (query: GetDesignsQueryDto) => {
  return useQuery({
    queryKey: [DESIGNS_QUERY_KEY],
    queryFn: () =>
      WBDApi.designs
        .designControllerGetDesigns({
          skip: query.skip ?? 0,
          take: query.take ?? 10,
          isFavorite: query.isFavorite
        })
        .then(({ data }) => data)
  })
}

export const fetchDesigns = async (query: GetDesignsQueryDto) => {
  const { data } = await WBDApi.designs.designControllerGetDesigns({
    skip: query.skip ?? 0,
    take: query.take ?? 10,
    isFavorite: query.isFavorite
  })
  return {
    items: data.items,
    total: data.count,
    favoritesCount: data.favoritesCount
  }
}

const ITEMS_PER_PAGE = 20

export const useInfiniteDesigns = (filters: { isFavorite?: boolean }) => {
  return useInfiniteQuery({
    queryKey: [DESIGNS_QUERY_KEY, filters],
    queryFn: ({ pageParam }) =>
      WBDApi.designs
        .designControllerGetDesigns({
          skip: pageParam,
          take: ITEMS_PER_PAGE,
          isFavorite: filters.isFavorite
        })
        .then(({ data }) => data),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.items.length,
        0
      )
      if (totalFetched >= lastPage.count) {
        return undefined
      }
      return totalFetched
    }
  })
}

export const useToggleFavoriteDesign = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      designId,
      isFavorite
    }: {
      designId: string
      isFavorite: boolean
    }) =>
      isFavorite
        ? WBDApi.designs.favoriteDesignControllerUnfavoriteDesign(designId)
        : WBDApi.designs.favoriteDesignControllerFavoriteDesign(designId),
    onSuccess: (_, { isFavorite }) => {
      toaster.create({
        title: isFavorite ? 'Removed from favorites' : 'Added to favorites',
        type: 'success'
      })
    },
    onError: () => {
      toaster.create({
        title: 'Error',
        description: 'Failed to update favorite status',
        type: 'error'
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [DESIGNS_QUERY_KEY] })
    }
  })
}

export const useDeleteDesign = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (designId: string) =>
      WBDApi.designs.designControllerDeleteDesign(designId),
    onSuccess: () => {
      toaster.create({
        title: 'Design deleted',
        description: 'Design has been deleted successfully',
        type: 'success'
      })
    },
    onError: () => {
      toaster.create({
        title: 'Error',
        description: 'Failed to delete design',
        type: 'error'
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [DESIGNS_QUERY_KEY] })
    }
  })
}

export const useDesignMockups = (designId: string | null) => {
  return useQuery({
    queryKey: ['design-mockups', designId],
    queryFn: () =>
      WBDApi.designs
        .designControllerGetDesignMockups(designId!)
        .then(({ data }) => data),
    enabled: !!designId,
    staleTime: 10_000
  })
}

export const useBulkDeleteDesigns = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(
        ids.map((id) => WBDApi.designs.designControllerDeleteDesign(id))
      )
    },
    onSuccess: () => {
      toaster.create({
        title: 'Designs deleted',
        type: 'success'
      })
      queryClient.invalidateQueries({ queryKey: [DESIGNS_QUERY_KEY] })
    },
    onError: () => {
      toaster.create({
        title: 'Error',
        description: 'Failed to delete designs',
        type: 'error'
      })
    }
  })
}
